import { Injectable } from '@angular/core';

/** Unread state the app icon badge is computed from; shared with push-sw.js. */
export interface BadgeState {
  chat: number;
  applications: number;
}

/** Must match BADGE_CACHE / BADGE_KEY in src/push-sw.js. */
export const BADGE_CACHE = 'bn-badge-v1';
export const BADGE_KEY = '/__bn-badge-state';
export const PUSH_SW_URL = '/push-sw.js';
export const PUSH_NAVIGATE_MESSAGE = 'bn-push-navigate';

/**
 * The browser capabilities push and the app badge depend on, behind one seam
 * so the services can be tested without a real service worker.
 */
@Injectable({ providedIn: 'root', useFactory: () => new BrowserPushPlatform() })
export abstract class PushPlatform {
  /** Service worker + Push API + Notifications are available. */
  abstract isPushSupported(): boolean;
  /** iPhone/iPad, where push needs the app on the home screen. */
  abstract isIos(): boolean;
  /** Opened from the home screen icon rather than in a browser tab. */
  abstract isStandalone(): boolean;
  abstract permission(): NotificationPermission;
  abstract requestPermission(): Promise<NotificationPermission>;
  abstract registerServiceWorker(): Promise<ServiceWorkerRegistration>;
  abstract onServiceWorkerMessage(handler: (data: unknown) => void): void;
  abstract setBadge(count: number): Promise<void>;
  abstract writeBadgeState(state: BadgeState): Promise<void>;
}

export class BrowserPushPlatform extends PushPlatform {
  isPushSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  isIos(): boolean {
    const ua = navigator.userAgent;
    return (
      /iPad|iPhone|iPod/.test(ua) ||
      (ua.includes('Macintosh') && navigator.maxTouchPoints > 1)
    );
  }

  isStandalone(): boolean {
    return (
      window.matchMedia?.('(display-mode: standalone)').matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true
    );
  }

  permission(): NotificationPermission {
    return 'Notification' in window ? Notification.permission : 'denied';
  }

  requestPermission(): Promise<NotificationPermission> {
    return Notification.requestPermission();
  }

  async registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    await navigator.serviceWorker.register(PUSH_SW_URL, { scope: '/' });
    return navigator.serviceWorker.ready;
  }

  onServiceWorkerMessage(handler: (data: unknown) => void): void {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', event =>
        handler(event.data)
      );
    }
  }

  async setBadge(count: number): Promise<void> {
    const nav = navigator as Navigator & {
      setAppBadge?: (count?: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };
    if (count > 0) {
      await nav.setAppBadge?.(count);
    } else {
      await nav.clearAppBadge?.();
    }
  }

  async writeBadgeState(state: BadgeState): Promise<void> {
    if (!('caches' in window)) return;
    const cache = await caches.open(BADGE_CACHE);
    await cache.put(
      BADGE_KEY,
      new Response(JSON.stringify(state), {
        headers: { 'Content-Type': 'application/json' },
      })
    );
  }
}
